import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { CLOCK } from 'app/core/constants/clock.token';
import { ROUTE_IDENTIFIER_NAMES } from 'app/core/models/route-identifiers/route-identifiers.model';
import { LoggedInUser } from 'app/core/models/person/logged-in-user.model';
import { AuthService } from 'app/core/services/auth/auth.service';
import { ConfigService } from 'app/core/services/config/config.service';
import {
  MenuCounts,
  MenuCountsService,
} from 'app/core/services/menu/menu-counts.service';
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  filter,
  forkJoin,
  map,
  Observable,
  of,
  startWith,
  switchMap,
  throttleTime,
} from 'rxjs';
import { ClaimRow } from './claims-v2.models';
import { LIST_ROW_OPTIONS, ClaimRowsService } from './claim-rows.service';
import { countsByCard } from './utils/claim-cards.util';

/** At most one recount per window, however fast the user navigates. */
export const MENU_COUNT_THROTTLE_MS = 30_000;
/** Cached rows older than this are refetched on the next recount. */
export const MENU_COUNT_MAX_AGE_MS = 60_000;

/** My Assessments: my drafts, plus the requests on its Waiting for me card. */
export function myAssessmentsCount(rows: ClaimRow[], meId: number): number {
  const counts = countsByCard(rows, meId);
  return counts.draft + counts.todo;
}

/** Pending My Assessment: the requests on its Waiting for me card. */
export function pendingMyAssessmentCount(
  rows: ClaimRow[],
  meId: number,
): number {
  return countsByCard(rows, meId).todo;
}

/**
 * Feeds the red "needs your action" pills on My Assessments and Pending My
 * Assessment (claims experience v2 only). Uses the shared ClaimRowsService
 * cache, so the counts always agree with the list page cards, and recounts on
 * navigation, throttled.
 */
@Injectable({ providedIn: 'root' })
export class ClaimMenuCountsService {
  private readonly config = inject(ConfigService);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly rows = inject(ClaimRowsService);
  private readonly menuCounts = inject(MenuCountsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly clock = inject(CLOCK);
  private started = false;
  private fetchedAt = 0;

  /** Idempotent. Does nothing while the flag is off. */
  start(): void {
    if (this.started || !this.config.claimsExperienceV2) {
      return;
    }
    this.started = true;

    const user$ = this.auth.loggedInUserSubj.pipe(
      distinctUntilChanged(
        (a, b) => a?.id === b?.id && a?.isAssessor === b?.isAssessor,
      ),
    );
    const navigated$ = this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      throttleTime(MENU_COUNT_THROTTLE_MS, undefined, {
        leading: true,
        trailing: true,
      }),
      startWith(null),
    );

    combineLatest([user$, navigated$])
      .pipe(
        switchMap(([user]) =>
          user ? this.count(user) : of<MenuCounts | null>(null),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((counts) => {
        if (counts) {
          this.menuCounts.set(counts);
        } else {
          this.menuCounts.clear([
            ROUTE_IDENTIFIER_NAMES.MY_ASSESSMENTS_LIST,
            ROUTE_IDENTIFIER_NAMES.ASSESSOR_ASSESSMENTS_LIST,
          ]);
        }
      });
  }

  private count(user: LoggedInUser): Observable<MenuCounts | null> {
    const now = this.clock().getTime();
    if (now - this.fetchedAt > MENU_COUNT_MAX_AGE_MS) {
      if (this.fetchedAt > 0) {
        this.rows.refresh();
      }
      this.fetchedAt = now;
    }
    // The same row set as the list pages, so the pills match their cards.
    const mine$ = this.rows
      .load('claimant', user.id, LIST_ROW_OPTIONS)
      .pipe(map(({ rows }) => myAssessmentsCount(rows, user.id)));
    const assessor$ = user.isAssessor
      ? this.rows
          .load('assessor', user.id, LIST_ROW_OPTIONS)
          .pipe(map(({ rows }) => pendingMyAssessmentCount(rows, user.id)))
      : of(0);
    return forkJoin([mine$, assessor$]).pipe(
      map(([mine, assessor]): MenuCounts => ({
        [ROUTE_IDENTIFIER_NAMES.MY_ASSESSMENTS_LIST]: mine,
        [ROUTE_IDENTIFIER_NAMES.ASSESSOR_ASSESSMENTS_LIST]: assessor,
      })),
      // A failed count is not worth an error: leave the pills as they were.
      catchError(() => EMPTY),
    );
  }
}
